'use client';

import { Field, HintText, Text } from '@ctct/summit-core';
import { get } from 'lodash';
import { type ComponentProps, type ElementType, type ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';

import { type InputOpts } from '../../types';
import { FormFieldTopRow } from './FormFieldTopRow';

interface FormFieldConfig<
  InputField extends ElementType = ElementType,
  FooterField extends ElementType = ElementType,
> {
  label?: string | ReactNode;
  description?: string | ReactNode;
  inputField: InputField;
  inputOpts: InputOpts & ComponentProps<InputField>;
  footerField?: FooterField;
  footerOpts?: InputOpts & ComponentProps<FooterField>;
  className?: string;
  isDisplayingMaxLength?: boolean;
}

const FormField = <InputField extends ElementType, FooterField extends ElementType>({
  fieldConfig,
}: {
  fieldConfig: FormFieldConfig<InputField, FooterField>;
}) => {
  const {
    inputOpts,
    footerOpts,
    inputField: InputFieldComponent,
    footerField: FooterFieldComponent,
    label,
    description,
    isDisplayingMaxLength,
    className,
  } = fieldConfig;
  const {
    formState: { errors },
  } = useFormContext();

  const inputError = get(errors, inputOpts.formKey);
  const footerError = footerOpts?.formKey ? get(errors, footerOpts.formKey) : undefined;
  const hasError = !!inputError || !!footerError;
  const errorId = `${inputError ? inputOpts.formKey : footerOpts?.formKey}-error`;
  const errorMessage = (inputError?.message ?? footerError?.message) as string;

  return (
    <Field
      state={hasError ? 'error' : 'available'}
      id={`${inputOpts.formKey}`}
      className={className}
    >
      <FormFieldTopRow
        label={label}
        isDisplayingMaxLength={isDisplayingMaxLength}
        maxLength={inputOpts.maxLength}
        formKey={inputOpts.formKey}
      />
      <InputFieldComponent {...(inputOpts as ComponentProps<InputField>)} />
      {description && !hasError && <Text className="r:text-xs">{description}</Text>}
      {hasError && (
        <HintText className="r:text-sm" id={errorId}>
          {errorMessage}
        </HintText>
      )}
      {FooterFieldComponent && (
        <FooterFieldComponent {...(footerOpts as ComponentProps<FooterField>)} />
      )}
    </Field>
  );
};

export { FormField, type FormFieldConfig };
